import { Controller } from '@hotwired/stimulus'
import { showFlash } from '../utils/flash_messages'

/**
 * CartSaveController
 * 
 * Handles saving cart descriptions via fetch API without full page reloads.
 * Provides user feedback through flash messages and updates UI dynamically.
 * 
 * Uses native fetch API instead of Turbo to properly handle JSON responses
 * and update the DOM manually.
 */
export default class extends Controller {
  static targets = ['form', 'input', 'submitButton']

  connect() {
    // Listen for form submit to handle via fetch
    this.element.addEventListener('submit', this.handleSubmit.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('submit', this.handleSubmit.bind(this))
  }

  /**
   * Handle form submission via fetch API
   * @param {Event} event - Form submit event
   */
  async handleSubmit(event) {
    event.preventDefault()
    
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }

    const form = event.target
    const formData = new FormData(form)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(form.action, {
        method: form.method.toUpperCase() === 'GET' ? 'GET' : 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData,
        credentials: 'same-origin'
      })

      const contentType = response.headers.get('content-type')
      const isJson = contentType && contentType.includes('application/json')

      if (!response.ok) {
        if (isJson) {
          const data = await response.json()
          this.handleError(data)
        } else {
          this.showFlashMessage('An error occurred while saving the cart', 'danger')
        }
        return
      }

      if (isJson) {
        const data = await response.json()
        this.handleJsonResponse(data)
      }
    } catch (error) {
      console.error('Cart save error:', error)
      this.showFlashMessage('An unexpected error occurred. Please try again.', 'danger')
    } finally {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
      }
    }
  }

  /**
   * Handle error response
   * @param {Object} data - Error response data
   */
  handleError(data) {
    const errorMessage = data.error || 'An error occurred while saving the cart'
    this.showFlashMessage(errorMessage, 'danger')
  }

  /**
   * Process JSON response from server
   * @param {Object} data - Response data from server
   */
  handleJsonResponse(data) {
    if (data.success) {
      // Update UI to show cart is now saved
      if (data.cartSaved && this.element.closest('.row')) {
        this.updateToSavedState(data)
        this.updateCartHeader(data)
      }
      
      // Show flash message AFTER UI updates to avoid duplicates
      this.showFlashMessage(data.message, 'success')
    } else if (data.error) {
      this.showFlashMessage(data.error, 'danger')
    }
  }

  /**
   * Update UI to reflect saved state
   * @param {Object} data - Response data containing cart info
   */
  updateToSavedState(data) {
    const container = this.element.closest('.col-12')
    if (!container) return

    // Use server-rendered HTML if available
    if (data.savedHtml) {
      container.innerHTML = data.savedHtml
    } else {
      // Fallback: Show management options
      const cartId = data.cartId
      const cartDescription = this.hasInputTarget ? this.inputTarget.value : 'Unnamed Cart'
      
      const managementUI = document.createElement('div')
      managementUI.className = 'border rounded p-3 bg-light'
      managementUI.innerHTML = `
        <h5 class="font-teko mb-3">Saved Cart Options</h5>
        
        <div class="mb-3" data-controller="cart-name-edit">
          <div data-cart-name-edit-target="display">
            <strong>Cart Name:</strong> <span data-cart-name-edit-target="nameLink">${cartDescription}</span>
            <button type="button" class="btn btn-link btn-sm text-primary p-0 ms-2" data-action="click->cart-name-edit#showEdit">
              <i class="bi bi-pencil"></i> Rename
            </button>
          </div>
          
          <div data-cart-name-edit-target="edit" class="d-none mt-2">
            <form action="/carts/${cartId}.json" method="post" data-turbo="false">
              <input type="hidden" name="_method" value="patch">
              <input type="hidden" name="authenticity_token" value="${this.getCSRFToken()}">
              <div class="input-group input-group-sm">
                <input type="text" name="description" value="${cartDescription}" maxlength="35" class="form-control" placeholder="Cart name" data-cart-name-edit-target="input">
                <button type="submit" class="btn btn-primary">
                  <i class="bi bi-check"></i> Save
                </button>
                <button type="button" class="btn btn-outline-secondary" data-action="click->cart-name-edit#cancelEdit">
                  <i class="bi bi-x"></i> Cancel
                </button>
              </div>
            </form>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <button type="button"
                  class="btn btn-sm btn-primary"
                  data-controller="cart-share"
                  data-action="click->cart-share#toggle"
                  data-cart-share-cart-id-param="${cartId}"
                  data-cart-share-share-param="true">
            <i class="bi bi-share"></i> Share with Company
          </button>
          
          <form action="/carts/${cartId}" method="post" data-turbo-confirm="Are you sure you want to unsave this cart? Your items will remain in your current cart.">
            <input type="hidden" name="authenticity_token" value="${this.getCSRFToken()}">
            <input type="hidden" name="_method" value="delete">
            <button type="submit" class="btn btn-sm btn-outline-danger">
              <i class="bi bi-x-circle"></i> Unsave Cart
            </button>
          </form>
          
          <a href="/carts" class="btn btn-sm btn-outline-primary">
            <i class="bi bi-list-ul"></i> Manage All Saved Carts
          </a>
        </div>
      `
      
      // Replace form container
      container.innerHTML = ''
      container.appendChild(managementUI)
    }
  }

  /**
   * Get CSRF token for forms
   * @returns {string} CSRF token
   */
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  /**
   * Update the cart header to show the saved cart name
   * @param {Object} data - Response data containing cart info
   */
  updateCartHeader(data) {
    const cartHeader = document.getElementById('cart-header')
    const cartName = document.getElementById('cart-name')
    const cartBadge = document.getElementById('cart-badge')
    const cartSubtitle = document.getElementById('cart-header-subtitle')
    
    if (cartHeader && cartName) {
      // Get the cart name from the input field, default to "Unnamed Cart" if blank
      const descriptionInput = this.hasInputTarget ? this.inputTarget : document.getElementById('cart_description')
      const inputValue = descriptionInput ? descriptionInput.value.trim() : ''
      const cartDescription = inputValue || 'Unnamed Cart'
      
      // Update the cart name
      cartName.textContent = cartDescription
      
      // Clear badge (don't show "Saved Cart" badge when not shared)
      if (cartBadge) {
        cartBadge.innerHTML = ''
      }
      
      // Show subtitle if it exists, otherwise create it
      if (!cartSubtitle) {
        const subtitle = document.createElement('p')
        subtitle.id = 'cart-header-subtitle'
        subtitle.className = 'text-muted mb-0'
        subtitle.style.fontSize = '0.9em'
        subtitle.innerHTML = '<i class="bi bi-info-circle"></i> Adding or removing items will automatically update this saved cart.'
        cartHeader.querySelector('.col-12').appendChild(subtitle)
      }
      
      // Show the header
      cartHeader.style.display = 'block'
      
      // Update page title to include "Saved Cart: " prefix
      document.title = `Saved Cart: ${cartDescription}`
    }
  }

  /**
   * Display flash message using centralized utility
   * @param {string} message - Message to display
   * @param {string} type - Message type (success, danger, warning, info)
   */
  showFlashMessage(message, type = 'success') {
    showFlash({ type, message })
  }
}
